/**
 * React Query Hooks for Progress Tracking
 * 
 * Custom hooks for fetching and managing progress data using React Query.
 * These hooks provide caching, loading states, and error handling.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { progressService } from '../services/progressService';
import {
  ProgressOverview,
  SubjectProgressDetail,
  ObjectiveProgressUpdate,
  ObjectiveProgressResponse,
  AssessmentStartResponse,
  AssessmentSubmission,
  AssessmentResultResponse,
  AssessmentHistoryItem,
  SubjectStudentsProgress,
  AdminProgressOverview,
  StudentProgressSummary,
} from '../types/progress';

// ============= QUERY KEYS =============

export const progressKeys = {
  all: ['progress'] as const,
  overview: () => [...progressKeys.all, 'overview'] as const,
  subject: (subjectId: string) => [...progressKeys.all, 'subject', subjectId] as const,
  assessmentHistory: () => [...progressKeys.all, 'assessmentHistory'] as const,
  // Admin keys
  adminOverview: () => [...progressKeys.all, 'admin', 'overview'] as const,
  subjectStudents: (subjectId: string) => [...progressKeys.all, 'admin', 'subject', subjectId, 'students'] as const,
  studentDetail: (studentId: string) => [...progressKeys.all, 'admin', 'student', studentId] as const,
  studentSubject: (studentId: string, subjectId: string) => 
    [...progressKeys.all, 'admin', 'student', studentId, 'subject', subjectId] as const,
  studentsBehind: () => [...progressKeys.all, 'admin', 'studentsBehind'] as const,
};

// ============= STUDENT HOOKS =============

/**
 * Hook to fetch overall progress overview for the current student
 */
export function useProgressOverview() {
  return useQuery<ProgressOverview, Error>({
    queryKey: progressKeys.overview(),
    queryFn: progressService.getProgressOverview,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to fetch detailed progress for a specific subject
 */
export function useSubjectProgress(subjectId: string | null) {
  return useQuery<SubjectProgressDetail, Error>({
    queryKey: progressKeys.subject(subjectId ?? ''),
    queryFn: () => progressService.getSubjectProgress(subjectId!),
    enabled: !!subjectId,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to update objective progress
 */
export function useUpdateObjectiveProgress() {
  const queryClient = useQueryClient();
  
  return useMutation<
    ObjectiveProgressResponse,
    Error,
    { objectiveId: string; update: ObjectiveProgressUpdate }
  >({
    mutationFn: ({ objectiveId, update }) => 
      progressService.updateObjectiveProgress(objectiveId, update),
    onSuccess: (data) => {
      // Invalidate related queries to refresh data
      queryClient.invalidateQueries({ queryKey: progressKeys.overview() });
      // We don't know which subject this belongs to, so invalidate all subject queries
      queryClient.invalidateQueries({ queryKey: [...progressKeys.all, 'subject'] });
    },
  });
}

/**
 * Hook to start an assessment
 */
export function useStartAssessment() {
  return useMutation<
    AssessmentStartResponse,
    Error,
    { sectionId: string; sectionType: 'topic' | 'subtopic' }
  >({
    mutationFn: ({ sectionId, sectionType }) => 
      progressService.startAssessment(sectionId, sectionType),
  });
}

/**
 * Hook to submit an assessment
 */
export function useSubmitAssessment() {
  const queryClient = useQueryClient();
  
  return useMutation<
    AssessmentResultResponse,
    Error,
    { assessmentId: string; submission: AssessmentSubmission }
  >({
    mutationFn: ({ assessmentId, submission }) => 
      progressService.submitAssessment(assessmentId, submission),
    onSuccess: () => {
      // Invalidate progress data to reflect updated mastery
      queryClient.invalidateQueries({ queryKey: progressKeys.overview() });
      queryClient.invalidateQueries({ queryKey: [...progressKeys.all, 'subject'] });
      queryClient.invalidateQueries({ queryKey: progressKeys.assessmentHistory() });
    },
  });
}

/**
 * Hook to fetch assessment history
 */
export function useAssessmentHistory(limit: number = 20) {
  return useQuery<AssessmentHistoryItem[], Error>({
    queryKey: [...progressKeys.assessmentHistory(), limit],
    queryFn: () => progressService.getAssessmentHistory(limit),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ============= ADMIN HOOKS =============

/**
 * Hook to fetch admin progress overview
 */
export function useAdminProgressOverview() {
  return useQuery<AdminProgressOverview, Error>({
    queryKey: progressKeys.adminOverview(),
    queryFn: progressService.getAdminProgressOverview,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to fetch all students' progress for a subject
 */
export function useSubjectStudentsProgress(subjectId: string | null) {
  return useQuery<SubjectStudentsProgress, Error>({
    queryKey: progressKeys.subjectStudents(subjectId ?? ''),
    queryFn: () => progressService.getSubjectStudentsProgress(subjectId!),
    enabled: !!subjectId,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to fetch detailed progress for a specific student
 */
export function useStudentProgressDetail(studentId: string | null) {
  return useQuery<ProgressOverview, Error>({
    queryKey: progressKeys.studentDetail(studentId ?? ''),
    queryFn: () => progressService.getStudentProgressDetail(studentId!),
    enabled: !!studentId,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Hook to fetch student's progress for a specific subject
 */
export function useStudentSubjectProgress(studentId: string | null, subjectId: string | null) {
  return useQuery<SubjectProgressDetail, Error>({
    queryKey: progressKeys.studentSubject(studentId ?? '', subjectId ?? ''),
    queryFn: () => progressService.getStudentSubjectProgress(studentId!, subjectId!),
    enabled: !!studentId && !!subjectId,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Hook to fetch students who are behind schedule
 */
export function useStudentsBehind(limit: number = 50) {
  return useQuery<StudentProgressSummary[], Error>({
    queryKey: [...progressKeys.studentsBehind(), limit],
    queryFn: () => progressService.getStudentsBehind(limit),
    staleTime: 5 * 60 * 1000,
  });
}

// ============= UTILITY HOOKS =============

/**
 * Hook to invalidate all progress queries (useful after major updates)
 */
export function useInvalidateProgress() {
  const queryClient = useQueryClient();
  
  return () => {
    queryClient.invalidateQueries({ queryKey: progressKeys.all });
  };
}

/**
 * Hook to prefetch subject progress (for smoother navigation)
 */
export function usePrefetchSubjectProgress() {
  const queryClient = useQueryClient();
  
  return (subjectId: string) => {
    queryClient.prefetchQuery({
      queryKey: progressKeys.subject(subjectId),
      queryFn: () => progressService.getSubjectProgress(subjectId),
      staleTime: 2 * 60 * 1000,
    });
  };
}

